// Small stand-ins for lodash, for plain data: arrays, plain objects and primitives, like GraphQL results

/** Deep copy; class instances like Date or Moment become plain objects */
export const cloneDeep = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(cloneDeep) as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneDeep(item)])) as T;
  }
  return value;
};

export const isEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b || typeof a !== "object" || typeof b !== "object" || Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const keys = Object.keys(aRecord);
  return (
    keys.length === Object.keys(bRecord).length &&
    keys.every((key) => Object.prototype.hasOwnProperty.call(bRecord, key) && isEqual(aRecord[key], bRecord[key]))
  );
};

export const groupBy = <T>(items: readonly T[], key: (item: T) => string): Record<string, T[]> => {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    (groups[key(item)] ??= []).push(item);
  }
  return groups;
};

const isMissing = (value: unknown) => value === null || value === undefined || Number.isNaN(value);

const compareValues = (a: any, b: any) => {
  if (isMissing(a) || isMissing(b)) {
    return Number(isMissing(a)) - Number(isMissing(b));
  }
  return a < b ? -1 : a > b ? 1 : 0;
};

/** Stable sort by each iteratee in turn, with null, undefined and NaN last */
export const sortBy = <T>(items: readonly T[] | null | undefined, iteratees: Array<(item: T) => unknown>): T[] =>
  [...(items ?? [])].sort((a, b) => {
    for (const iteratee of iteratees) {
      const order = compareValues(iteratee(a), iteratee(b));
      if (order !== 0) {
        return order;
      }
    }
    return 0;
  });
